import { BrowserModule, DomSanitizer, HammerModule, Title } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AppRoutingModule } from './app-routing.module';
import { CookieService } from 'ng2-cookies';
import { MaterialModule } from './material/material.module';
import { SigninComponent } from './landing/signin/signin.component';
import { SignupComponent } from './landing/signup/signup.component';
import { HomeComponent } from './home/home.component';
import { TextInputComponent } from './component/text-input/text-input.component';
import { PasswordInputComponent } from './component/password-input/password-input.component';
import { RecoverPasswordComponent } from './landing/recover-password/recover-password.component';
import { NavbarComponent } from './component/navbar/navbar.component';
import { SidebarComponent } from './component/sidebar/sidebar.component';
import { LandingComponent } from './landing/landing.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { BaseComponent } from './base/base.component';
import { EmailConfirmedComponent } from './landing/email-confirmed/email-confirmed.component';
import { SelectInputComponent } from './component/select-input/select-input.component';
import { SelectInputLoadEventComponent } from './component/select-input-le/select-input-le.component';
import { SelectInputLoadFlagComponent } from './component/select-input-flag/select-input-flag.component';
import { CalendarComponent } from './home/calendar/calendar.component';
import { SubmissionsComponent } from './home/submissions/submissions.component';
import { ReviewsComponent } from './home/reviews/reviews.component';
import { CommitteesComponent } from './home/committees/committees.component';
import { SessionChairingsComponent } from './home/session-chairings/session-chairings.component';
import { PublicationChairingsComponent } from './home/publication-chairings/publication-chairings.component';
import { MyEventsComponent } from './home/my-events/my-events.component';
import { EventsComponent } from './home/events/events.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { EmailInputComponent } from './component/email-input/email-input.component';
import { Environment } from './environment/environment';
import { FabComponent } from './component/fab/fab.component';
import { AuthGuard } from './guard/auth.guard';
import { RedirectGuard } from './guard/redirect.guard';
import { ChangePasswordComponent } from './landing/change-password/change-password.component';
import { ProfileComponent } from './component/profile/profile.component';
import { AboutComponent } from './home/about/about.component';
import { AboutV2Component } from './home/about-v2/about-v2.component';
import { HelpComponent } from './home/help/help.component';
import { SettingsComponent } from './home/settings/settings.component';
import { ClickOutsideModule } from 'ng-click-outside';
import { SubmissionViewComponent } from './component/submission-view/submission-view.component';
import { SubmissionFormViewComponent } from './component/submission-form-view/submission-form-view.component';
import { TopicsComponent } from './component/topics/topics.component';
import { CreateSubmissionComponent } from './component/create-submission/create-submission.component';
import { MultilineTextInputComponent } from './component/multiline-text-input/multiline-text-input.component';
import { FileUploadComponent } from './component/file-upload/file-upload.component';
import { FilePillComponent } from './component/file-pill/file-pill.component';
import { PopoverComponent } from './component/popover/popover.component';
import { GroupMenuComponent } from './component/group-menu/group-menu.component';
import { StatusMenuComponent } from './component/status-menu/status-menu.component';
import { JemsSelectComponent } from './component/jems-select/jems-select.component';
import { JemsTextareaComponent } from './component/jems-textarea/jems-textarea.component';
import { StatusSelectComponent } from './component/status-select/status-select.component';
import { JemsListboxDirective, JemsListboxOptionDirective } from './component/listbox/listbox.directive';
import { ThresholdBandComponent } from './component/threshold-band/threshold-band.component';
import { IeeeElectronicCopyrightComponent } from './component/ieee-electronic-copyright/ieee-electronic-copyright.component';
import { FileDownloadComponent } from './component/file-download/file-download.component';
import { FilePillsComponent } from './component/file-pills/file-pills.component';
import { GoToElementComponent } from './component/go-to-element/go-to-element.component';
import { UserSearchComponent } from './component/user-search/user-search.component';
import { UserSearchConflictsComponent } from './component/user-search-conflicts/user-search-conflicts.component';
import { AffiliationSearchComponent } from './component/affiliation-search/affiliation-search.component';
import { SearchButtonComponent } from './component/search-button/search-button.component';
import { ReviewDelegateComponent } from './component/review-delegate/review-delegate.component';
import { UserSearchDialogComponent } from './component/user-search-dialog/user-search-dialog.component';
import { VerifySimilarityDialogComponent} from './component/verify-similarity-dialog/verify-similarity-dialog.component';
import { ReviewFormComponent } from './component/review-form/review-form.component';
import { CardViewComponent } from './component/card-view/card-view.component';
import { ChipCheckboxComponent } from './component/chip-checkbox/chip-checkbox.component';
import { SubmissionsTableComponent } from './component/submissions-table/submissions-table.component';
import { EventComponent } from './component/event/event.component';
import { EventViewComponent } from './component/event-view/event-view.component';
import { SubmissionStepComponent } from './component/submission-step/submission-step.component';
import { ErrorComponent } from './component/error/error.component';
import { UserFilterPipe } from './pipe/user-filter.pipe';
import { RadioButtonComponent } from './component/radio-button/radio-button.component';
import { FloatingSubmitBarComponent } from './component/floating-submit-bar/floating-submit-bar.component';
import { PeopleTableComponent } from './component/people-table/people-table.component';
import { ChairTabsComponent } from './component/chair-tabs/chair-tabs.component';
import { ChairTabComponent } from './component/chair-tabs/chair-tab.component';
import { LazyLoadDirective } from './directive/lazy-load.directive';
import { FixedPrefixDirective } from './directive/fixed-prefix.directive';
import {
  PeopleTableBatchActionsDirective,
  PeopleTableFooterAddDirective,
  PeopleTableRowActionsDirective,
} from './component/people-table/people-table.directives';
import { CheckboxInputComponent } from './component/checkbox-input/checkbox-input.component';
import { CustomFormComponent } from './component/custom-form/custom-form.component';
import { EmptyStateComponent } from './component/empty-state/empty-state.component';
import { ReadableSizePipe } from './pipe/readable-size.pipe';
import { TruncatePipe } from './pipe/truncate.pipe';
import { CurrencyFormatPipe } from './pipe/currency.format.pipe';
import { NumericInputComponent } from './component/numeric-input/numeric-input.component';
import { ReviewViewComponent } from './component/review-view/review-view.component';
import { UserReviewComponent } from './component/user-review/user-review.component';
import { FixedTopProgressBarComponent } from './component/fixed-top-progress-bar/fixed-top-progress-bar.component';
import { MAT_PROGRESS_BAR_LOCATION } from '@angular/material/progress-bar';
import { FileRulesTableComponent } from './component/file-rules-table/file-rules-table.component';
import { SidesheetComponent } from './component/sidesheet/sidesheet.component';
import { ZerofillPipe } from './pipe/zerofill.pipe';
import { AvatarModule } from 'ngx-avatar';
import { AdminComponent } from './admin/admin.component';
import { EcoFabSpeedDialModule } from '@ecodev/fab-speed-dial';
import { AdminEventsListComponent } from './admin/events/events.component';
import { AdminEventViewComponent } from './admin/event-admin/event-view/event-view.component';
import { EventAdminComponent } from './admin/event-admin/event-admin.component';
import { EventSubmissionsComponent } from './admin/event-admin/event-submissions/event-submissions.component';
import { EventPeopleComponent } from './admin/event-admin/event-people/event-people.component';
import { EventSettingsComponent } from './admin/event-admin/event-settings/event-settings.component';
import { SidebarItemComponent } from './component/sidebar-item/sidebar-item.component';
import { AdminEventSidebarComponent } from './component/admin-event-sidebar/admin-event-sidebar.component';
import { EventBreadcrumbComponent } from './component/event-breadcrumb/event-breadcrumb.component';
import { SidebarItemAdminComponent } from './component/sidebar-item-admin/sidebar-item-admin.component';
import { RouterLinkDisableDirective } from './directive/router-link-disable.directive';
import { OverflowTooltipDirective } from './directive/overflow-tooltip.directive';
import { TruncateDirective } from './directive/truncate.directive';
import { CollapseHeightDirective } from './directive/collapse-height.directive';
import { StickyShadowDirective } from './directive/sticky-shadow.directive';
import { StepperAutoHeightDirective } from './directive/stepper-auto-height.directive';
import { EventsFilterPipe } from './pipe/events-filter.pipe';
import { DefaultFilterPipe } from './pipe/default-filter.pipe';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { AdminNavbarComponent } from './component/admin-navbar/admin-navbar.component';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { CardPlaceholderComponent } from './component/card-placeholder/card-placeholder.component';
import { ReplacePipe } from './pipe/replace.pipe';
import { SubmissionsListFilterComponent } from './component/submissions-list-filter/submissions-list-filter.component';
import { RequestInterceptor } from './interceptor/http-request.interceptor';
import { ErrorSnackbarComponent } from './component/error-snackbar/error-snackbar.component';
import { SearchResultsComponent } from './home/search-results/search-results.component';
import { AdminEventCreateComponent } from './admin/events/create-event/create-event.component';
import { FormOverlayComponent } from './component/form-overlay/form-overlay.component';
import { DatepickerInputComponent } from './component/datepicker-input/datepicker-input.component';
import { DatetimepickerInputComponent } from './component/datetimepicker-input/datetimepicker-input.component';
import { JemsCalendarHeaderComponent } from './component/datetimepicker-input/jems-calendar-header.component';
import { ArrayChipInputComponent } from './component/array-chip-input/array-chip-input.component';
import { EventSearchComponent } from './component/event-search/event-search.component';
import { NotificationSnackbarComponent } from './component/notification-snackbar/notification-snackbar.component';
import { AdminGuard } from './guard/admin.guard';
import { ErrorViewComponent } from './component/error-view/error-view.component';
import { ViewPreferencesPickerComponent } from './component/view-preferences-picker/view-preferences-picker.component';
import { ChangePasswordProfileComponent } from './component/change-password-profile/change-password-profile.component';
import { SubmissionCardNewComponent } from './component/submission-card-new/submission-card-new.component';
import { ReviewCardNewComponent } from './component/review-card-new/review-card-new.component';
import { SelectionActionsBarComponent } from './component/selection-actions-bar/selection-actions-bar.component';
import { PaperStatusComponent } from './component/paper-status/paper-status.component';
import { ChangeProfilePictureComponent } from './component/change-profile-picture/change-profile-picture.component';
import { SignoutComponent } from './landing/signout/signout.component';
import { LoadingComponent } from './home/loading/loading.component';
import { AssignReviewersComponent } from './component/assign-reviewers/assign-reviewers.component';
import { CharacterCounterComponent } from './component/character-counter/character-counter.component';
import { RequestEventComponent } from './home/request/event/event.component';
import { EventFormComponent } from './component/event-form/event-form.component';
import { AdminEventRequestsComponent } from './admin/event-requests/event-requests.component';
import { ObjectToArrayPipe } from './pipe/object-to-array.pipe';
import { CheckEventComponent } from './component/check-event/check-event.component';
import { AdminLandingComponent } from './admin/landing/landing.component';
import { AdminSidebarComponent } from './component/admin-sidebar/admin-sidebar.component';
import { AdminPageHeaderComponent } from './component/admin-page-header/admin-page-header.component';
import { SegmentedControlComponent } from './component/segmented-control/segmented-control.component';
import { AdminEventRequestComponent } from './admin/event-requests/request/request.component';
import { SubmissionEditComponent } from './component/submission-edit/submission-edit.component';
import { EventSettingsEmailComponent } from './admin/event-admin/event-settings/event-settings-email/event-settings-email.component';
import { ActivateEmailComponent } from './landing/activate-email/activate-email.component';
import { EventTopicsComponent } from './admin/event-admin/event-settings/event-topics/event-topics.component';
import { EventChairsComponent } from './admin/event-admin/event-people/event-chairs/event-chairs.component';
import { EventPublicationChairsComponent } from './admin/event-admin/event-people/event-publication-chairs/event-publication-chairs.component';
import { EventTracksComponent } from './admin/event-admin/event-settings/event-tracks/event-tracks.component';
import { EventTracksEditComponent } from './component/event-tracks-edit/event-tracks-edit.component';
import { AdminEventNavbarComponent } from './component/admin-event-navbar/admin-event-navbar.component';
import { EventSendEmailComponent } from './admin/event-admin/event-people/event-send-email/event-send-email.component';
import { EventMembersComponent } from './admin/event-admin/event-people/event-members/event-members.component';
import { TpcInvitePanelComponent } from './component/tpc-invite-panel/tpc-invite-panel.component';
import { PaperGroupsComponent } from './admin/event-admin/event-settings/paper-groups/paper-groups.component';
import { PaperTitlePipe } from './pipe/paper.title.pipe';
import { AdminCreateSubmissionComponent } from './component/admin-create-submission/admin-create-submission.component';
import { AdminSubmitSelectTrackComponent } from './admin/event-admin/admin-submit-select-track/admin-submit-select-track.component';
import { SearchResultsInEventComponent } from './admin/event-admin/search-results-in-event/search-results-in-event.component';
import { ReviewsConfigurationComponent } from './admin/event-admin/event-reviews/reviews-configuration/reviews-configuration.component';
import { PeopleCardComponent } from './component/people-card/people-card.component';
import { UserEventTopicsComponent } from './home/user-event-topics/user-event-topics.component';
import { EventUserTopicsComponent } from './admin/event-admin/event-people/event-user-topics/event-user-topics.component';
import { EventFormsComponent } from './admin/event-admin/event-settings/event-forms/event-forms.component';
import { TPCGroupsComponent } from './admin/event-admin/event-settings/tpc-groups/tpc-groups.component';
import { FormQuestionComponent } from './component/form-question/form-question.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayModule } from '@angular/cdk/overlay';
import { EventTitlePipe } from './pipe/event.title.pipe';
import { ChairOrAdminGuard } from './guard/chair-admin.guard';
import { CustomFormTextQuestionComponent } from './component/custom-form-text-question/custom-form-text-question.component';
import { CustomFormCheckboxQuestionComponent } from './component/custom-form-checkbox-question/custom-form-checkbox-question.component';
import { CustomFormRadioQuestionComponent } from './component/custom-form-radio-question/custom-form-radio-question.component';
import { CustomFormLabelQuestionComponent } from './component/custom-form-label-question/custom-form-label-question.component';
import { ReportPrintingProblemComponent } from './component/report-printing-problem/report-printing-problem.component';
import { PreviewFormComponent } from './admin/event-admin/event-settings/event-forms/preview-form/preview-form.component';
import { EditableInputComponent } from './component/editable-input/editable-input.component';
import { PaperInfoComponent } from './component/paper-info/paper-info.component';
import { SubmissionTopicsDisplayComponent } from './component/submission-topics-display/submission-topics-display.component';
import { SubmissionAbstractDisplayComponent } from './component/submission-abstract-display/submission-abstract-display.component';
import { SubmissionSessionDisplayComponent } from './component/submission-session-display/submission-session-display.component';
import { SubmissionHeaderComponent } from './component/submission-header/submission-header.component';
import { SubmissionAuthorsDisplayComponent } from './component/submission-authors-display/submission-authors-display.component';
import { SubmissionExtraFieldsDisplayComponent } from './component/submission-extra-fields-display/submission-extra-fields-display.component';
import { SubmissionExportViewComponent } from './component/submission-export-view/submission-export-view.component';
import { PresentationCertificateComponent } from './component/presentation-certificate/presentation-certificate.component';
import { CertificateSheetComponent } from './component/certificate-sheet/certificate-sheet.component';
import { CertificateTemplateEditorComponent } from './admin/event-admin/event-submissions/certificate-template/certificate-template-editor.component';
import { StartEditReviewComponent } from './component/start-edit-review/start-edit-review.component';
import { PaperReviewActionsComponent } from './component/paper-review-actions/paper-review-actions.component';
import { PaperReviewListComponent } from './component/paper-review-list/paper-review-list.component';
import { MissingManuscriptsComponent } from './admin/event-admin/event-settings/missing-manuscripts/missing-manuscripts.component';
import { UserViewComponent } from './admin/event-admin/event-people/user-view/user-view.component';
import { PrintingProblemComponent } from './component/printing-problem/printing-problem.component';
import { PaperReviewHistoryComponent } from './component/paper-review-history/paper-review-history.component';
import { SubmissionHistoryComponent } from './component/submission-history/submission-history.component';
import { ConflictInterestComponent } from './home/conflict-interest/conflict-interest.component';
import { ConfirmationDialogComponent } from './component/confirmation-dialog/confirmation-dialog.component';
import { ReviewConfirmationDialogComponent } from './component/review-confirmation-dialog/review-confirmation-dialog.component';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatPaginatorI18n } from './class/mat-paginator-i18n';
import { NgxPrintModule } from 'ngx-print';
import { ReviewStatusPerSubmissionComponent } from './admin/event-admin/event-reviews/review-status-per-submission/review-status-per-submission.component';
import { ReviewStatusPerUserComponent } from './admin/event-admin/event-reviews/review-status-per-user/review-status-per-user.component';
import { ConfirmReviewComponent } from './landing/confirm-review/confirm-review.component';
import { ConfirmTPCInviteRedirectComponent } from './landing/confirm-tpcinvite-redirect/confirm-tpcinvite-redirect.component';
import { NotifyReviewersComponent } from './component/notify-reviewers/notify-reviewers.component';
import { AdminNotifyReviewersComponent } from './admin/event-admin/event-reviews/notify-reviewers/notify-reviewers.component';
import { NotifyAuthorsComponent } from './component/notify-authors/notify-authors.component';
import { AdminNotifyAuthorsComponent } from './admin/event-admin/event-submissions/notify-authors/notify-authors.component';
import { ClaimReviewComponent } from './component/claim-review/claim-review.component';
import { SubmissionFieldsFilterComponent } from './component/submission-fields-filter/submission-fields-filter.component';
import { FindReviewersByInterestComponent } from './component/find-reviewers-by-interest/find-reviewers-by-interest.component';
import { ReviewerFinderComponent } from './component/reviewer-finder/reviewer-finder.component';
import { EventUserViewComponent } from './component/event-user-view/event-user-view.component';
import { CreateUserComponent } from './component/create-user/create-user.component';
import { TracksComponent } from './component/tracks/tracks.component';
import { FileRulesViewComponent } from './component/file-rules-view/file-rules-view.component';
import { EventBackButtonComponent } from './component/event-back-button/event-back-button.component';
import { EditUserAffiliationComponent } from './component/edit-user-affiliation/edit-user-affiliation.component';
import { SubmissionRankingComponent } from './admin/event-admin/event-submissions/submission-ranking/submission-ranking.component';
import { AuthorsListComponent } from './admin/event-admin/event-submissions/authors-list/authors-list.component';
import { SubmissionsUploadComponent } from './admin/event-admin/event-submissions/submissions-upload/submissions-upload.component';
import { ReviewsUploadComponent } from './admin/event-admin/event-reviews/reviews-upload/reviews-upload.component';
import { AssignReviewSuggestionComponent } from './admin/event-admin/event-reviews/assign-review-suggestion/assign-review-suggestion.component';
import { NgxMatDatetimePickerModule, NgxMatTimepickerModule, NgxMatNativeDateModule, NGX_MAT_DATE_FORMATS } from '@angular-material-components/datetime-picker';
import { NgxMatMomentModule } from '@angular-material-components/moment-adapter';
import { SubmissionWithdrawalComponent } from './component/submission-withdrawal/submission-withdrawal.component';
import { SubmissionDeleteComponent } from './component/submission-delete/submission-delete.component';
import { DiscussionMessagesComponent } from './component/discussion-messages/discussion-messages.component';
import { EmailPreviewComponent } from './component/email-preview/email-preview.component';
import { SubmissionFilesComponent } from './admin/event-admin/event-submissions/submission-files/submission-files.component';
import { from, Observable } from 'rxjs';
import { AdminBibtexExportComponent } from './admin/event-admin/event-submissions/bibtex-export/bibtex-export.component';
import { ClipboardModule } from 'ngx-clipboard';
import { RouteTitleGuard } from './guard/route-title.guard';
import { UnsavedChangesGuard } from './guard/unsaved-changes.guard';
import { AdminDefaultEmailTemplatesComponent } from './admin/admin-default-email-templates/admin-default-email-templates.component';
import { RebuttalComponent } from './component/rebuttal/rebuttal.component';
import { AdministratorsComponent } from './admin/administrators/administrators.component';
import { MergeUsersComponent } from './admin/merge-users/merge-users.component';
import { AdminDeletedSubmissionsComponent } from './admin/deleted-submissions/deleted-submissions.component';
import { ProxyBarComponent } from './component/proxy-bar/proxy-bar.component';
import { AdminEventWizardComponent } from './admin/event-admin/event-wizard/event-wizard.component';
import { FileRulesCompactComponent } from './component/file-rules-compact/file-rules-compact.component';
import { ChartistModule } from 'ng-chartist';
import { ChartComponent } from './component/chart-component/chart-component.component';
import { DashboardChartComponent } from './component/dashboard-chart/dashboard-chart.component';
import { AdminEventSubconferencesComponent } from './admin/event-admin/event-subconferences/event-subconferences.component';
import { MarkdownModule } from 'ngx-markdown';
import { ShowControlsErrorsComponent } from './component/show-controls-errors/show-controls-errors.component';
import { UserDashboardComponent } from './home/user-dashboard/user-dashboard.component';
import { ReviewsSmallTableComponent } from './component/reviews-small-table/reviews-small-table.component';
import { SessionsComponent } from './component/sessions/sessions.component';
import { CalendarViewComponent } from './admin/event-admin/event-sessions/calendar-view/calendar-view.component';
import { EventSessionsComponent } from './admin/event-admin/event-sessions/sessions/sessions.component';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import { NgxMailtoModule } from 'ngx-mailto';
import * as moment from 'moment';
import { GeneralSessionDialogComponent } from './component/general-session-dialog/general-session-dialog.component';
import { FormSessionDialogComponent } from './component/form-session-dialog/form-session-dialog.component';
import { FormSubSessionDialogComponent } from './component/sub-session-dialog/sub-session-dialog.component';
import { EventProgramComponent } from './admin/event-admin/event-sessions/program/event-program.component';
import { EmailStatusComponent } from './admin/event-admin/event-settings/email-status/email-status.component';
import { CountryPipe } from './pipe/country.pipe';
import { CountryPurePipe } from './pipe/country-pure.pipe';
import { DateFormatPurePipe } from './pipe/date-format-pure.pipe';
import { UserAffiliationPipe } from './pipe/user-affiliation.pipe';
import { LandingProfileComponent } from './component/landing-profile/landing-profile.component';
import { LoginHistoryComponent } from './component/login-history/login-history.component';
import { DateFormatPipe } from '../app/pipe/date.format.pipe';
import { RelativeTimePipe } from './pipe/relative-time.pipe';
import { EventUrlPipe } from './pipe/event-url.pipe';
import ptBRLocale from '@angular/common/locales/pt';
import { registerLocaleData } from '@angular/common';
import { EVENT_MANAGER_PLUGINS } from '@angular/platform-browser';
import { PassiveTouchEventsPlugin } from './lib/passive-touch-events.plugin';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { ImageCropperModule } from 'ngx-image-cropper';
import { SubSessionTextFilterPipe } from './pipe/session-sub-filter';
import { SubSessionStatusFilterPipe } from './pipe/session-status-sub-filter';
import { CapitalizeFirstPipe } from './pipe/capitalize-first.pipe';
import { ColorPickerComponent } from './component/color-picker/color-picker.component';
import { SessionSubsOrderPipe } from './pipe/session-subs-order';
import { SessionSubmissionsPickerComponent } from './component/session-submissions-picker/session-submissions-picker.component';
import { AddSessionSubmissionsDialogComponent } from './component/add-session-submissions-dialog/add-session-submissions-dialog.component';
import { AvatarProfileComponent } from './component/avatar-profile/avatar-profile.component';
import { NumberInputComponent } from './component/number-input/number-input.component';
import { SubmissionStatisticsComponent } from './admin/event-admin/event-submissions/submission-statistics/submission-statistics.component';
import { SubmissionTopicStatisticsComponent } from './component/submission-topic-statistics/submission-topic-statistics.component';
import { SubmissionTracksStatisticsComponent } from './component/submission-tracks-statistics/submission-tracks-statistics.component';
import { SubmissionAuthorStatisticsComponent } from './component/submission-author-statistics/submission-author-statistics.component';
import { InputFilterAutocompleteComponent } from './component/input-filter-autocomplete/input-filter-autocomplete.component';
import { EventPublicationComponent } from './admin/event-admin/event-publication/event-publication/event-publication.component';
import { EventPublicationProceedingsComponent } from './admin/event-admin/event-publication/event-publication-proceedings/event-publication-proceedings.component';
import { CustomControlFieldEditComponent } from './component/custom-control-field-edit/custom-control-field-edit.component';
import { DialogTimezoneComponent } from './component/dialog-timezone/dialog-timezone.component';
import { CreateAffiliationComponent } from './component/create-affiliation/create-affiliation.component';
import { SubmissionsListTableComponent } from './component/submissions-list-table/submissions-list-table.component';
import { ProceedingStepComponent } from './component/proceeding-step/proceeding-step.component';
import { ProceedingContentItemComponent } from './component/proceeding-content-item/proceeding-content-item.component';
import { FileRulesCardComponent } from './component/file-rules-card/file-rules-card.component';
import { NewGroupDialogComponent } from './component/new-group-dialog/new-group-dialog.component';
import { FileRulesCardInfoDialogComponent } from './component/file-rules-card-info-dialog/file-rules-card-info-dialog.component';
import { ArticlesListCardDialogComponent } from './component/articles-list-card-dialog/articles-list-card-dialog.component';
import { SelectInputGroupComponent } from './component/select-input-group/select-input-group.component';
import { PublicationFixedControlComponent } from './component/publication-fixed-control/publication-fixed-control.component';
import { SubmissionPublicationSectionComponent } from './component/submission-publication-section/submission-publication-section.component';
import { MenuSubmissionListComponent } from './component/menu-submission-list/menu-submission-list.component';
import { ActionsMenuButtonComponent } from './component/actions-menu-button/actions-menu-button.component';
import { NotifyPendingsComponent } from './component/notify-pendings/notify-pendings.component';
import { ProxyHistoryComponent } from './component/proxy-history/proxy-history.component';
import { DisplayPaperTitleComponent } from './component/display-paper-title/display-paper-title.component';
import { DialogCreateUserComponent } from './component/dialog-create-user/dialog-create-user.component';
import { PrintingProblemsComponent } from './component/printing-problems/printing-problems.component';
import { ProceedingIndexItemComponent } from './component/proceeding-index-item/proceeding-index-item.component';
import { TimeZoneInputComponent } from './component/time-zone-input/time-zone-input.component';
import { PaperPublicationInfoComponent } from './component/paper-publication-info/paper-publication-info.component';
import { ProceedingAuthorIndexItemComponent } from './component/proceeding-author-index-item/proceeding-author-index-item.component';
import { ReviewerInvitationComponent } from './component/reviewer-invitation/reviewer-invitation.component';
import { InputDropFileComponent } from './component/input-drop-file/input-drop-file.component';
import { ChangeTracksDialogComponent } from './component/change-tracks-dialog/change-tracks-dialog.component';
import { ReviewReclaimComponent } from './component/review-reclaim/review-reclaim.component';
import { AdminUserSearchComponent } from './admin/user-search/user-search.component';
import { SubmissionsListTableV2Component } from './component/submissions-list-table-v2/submissions-list-table-v2.component';
import { ScoreCellComponent } from './component/score-cell/score-cell.component';
import { ScrollTopFabComponent } from './component/scroll-top-fab/scroll-top-fab.component';
import { SubmissionsFieldFilterV2Component } from './component/submissions-field-filter-v2/submissions-field-filter-v2.component';
import { ChartComponentV2Component } from './component/chart-component-v2/chart-component-v2.component';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { DashboardChartV2Component } from './component/dashboard-chart-v2/dashboard-chart-v2.component';
import { ExportSubmissionsComponent } from './admin/event-admin/event-submissions/export-submissions/export-submissions.component';
import { ExportSubmissionsChooserComponent } from './admin/event-admin/event-submissions/export-submissions-chooser/export-submissions-chooser.component';
import { RECAPTCHA_SETTINGS, RecaptchaModule, RecaptchaSettings } from 'ng-recaptcha';
import { MathCaptchaComponent } from './component/math-captcha/math-captcha.component';
import { EditEmailComponent } from './component/edit-email/edit-email.component';
import { MatIconRegistry } from '@angular/material/icon';
import { DeletedSubmissionsComponent } from './component/deleted-submissions/deleted-submissions.component';
import { ImportEmailTemplatesDialogComponent } from './component/import-email-templates-dialog/import-email-templates-dialog.component';
import { EmailTemplateEditDialogComponent } from './component/email-template-edit-dialog/email-template-edit-dialog.component';
import { VariableTemplateEditorComponent } from './component/variable-template-editor/variable-template-editor.component';
import { EmailTemplatePreviewComponent } from './component/email-template-preview/email-template-preview.component';
import { CustomSearchComponent } from './component/custom-search/custom-search.component';
import { SupportButtonComponent } from './support-button/support-button.component';
import { HelpdeskCardComponent } from './component/helpdesk-card/helpdesk-card.component';
import { HelpDialogComponent } from './component/help-dialog/help-dialog.component';
import { CommandPaletteComponent } from './component/command-palette/command-palette.component';
import { TrackFileLabelComponent } from './component/track-file-label/track-file-label.component';
import { UserNotificationsComponent } from './component/user-notifications/user-notifications.component';
import { UserNotificationPreviewComponent } from './component/user-notification-preview/user-notification-preview.component';
import { NotificationsCenterComponent } from './component/notifications-center/notifications-center.component';


export function momentAdapterFactory() {
  return adapterFactory(moment);
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export class LazyTranslateLoader implements TranslateLoader {
  getTranslation(lang: string): Observable<any> {
    return from(import(`../assets/i18n/${lang}.json`));
  }
}

export function jwtTokenGetter() {
  return localStorage.getItem('token');
}

const API = (new URL(Environment.urls.API));

export function getJwtConfig() {
  return {
    tokenGetter: jwtTokenGetter,
    allowedDomains: [API.host],
    disallowedRoutes: [
      Environment.urls.API + '/core/auth/login/',
      Environment.urls.API + '/core/auth/token/refresh/',
      Environment.urls.API + '/core/auth/password/reset/confirm/',
      Environment.urls.API + '/core/auth/password/reset/',
      Environment.urls.API + '/core/auth/activate/',
      Environment.urls.API + '/core/user/',
      new RegExp(escapeRegExp(Environment.urls.API + '/core/institution/search/?search=') + '.*'),
      Environment.urls.API + '/core/institution/',
      Environment.urls.API + '/core/countries/'
    ]
  };
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

// mat-progress-bar monta `url({pathname}#{id})` pro pattern SVG. O
// {pathname} vira URL absoluta e Chrome tenta baixar como arquivo (404).
// Bug conhecido em SPAs. Fix: pathname vazio → referência local `#{id}`.
export function getPathnameMock() {
  return '';
}

registerLocaleData(ptBRLocale);

export { getLocale } from './util/locale';
import { getLocale } from './util/locale';

@NgModule({
  declarations: [
    SignoutComponent,
    LoadingComponent,
    SigninComponent,
    SignupComponent,
    HomeComponent,
    TextInputComponent,
    PasswordInputComponent,
    RecoverPasswordComponent,
    NavbarComponent,
    SidebarComponent,
    LandingComponent,
    BaseComponent,
    EmailConfirmedComponent,
    SelectInputComponent,
    SelectInputLoadEventComponent,
    SelectInputLoadFlagComponent,
    CalendarComponent,
    SubmissionsComponent,
    ReviewsComponent,
    CommitteesComponent,
    SessionChairingsComponent,
    PublicationChairingsComponent,
    MyEventsComponent,
    EventsComponent,
    EmailInputComponent,
    FabComponent,
    SubmissionViewComponent,
    SubmissionFormViewComponent,
    ProfileComponent,
    CalendarViewComponent,
    AboutComponent,
    AboutV2Component,
    HelpComponent,
    SettingsComponent,
    ChangePasswordComponent,
    TopicsComponent,
    CreateSubmissionComponent,
    MultilineTextInputComponent,
    FileUploadComponent,
    FilePillComponent,
    PopoverComponent,
    GroupMenuComponent,
    StatusMenuComponent,
    StatusSelectComponent,
    JemsSelectComponent,
    JemsTextareaComponent,
    JemsListboxDirective,
    JemsListboxOptionDirective,
    ThresholdBandComponent,
    IeeeElectronicCopyrightComponent,
    FileDownloadComponent,
    FilePillsComponent,
    GoToElementComponent,
    ZerofillPipe,
    UserSearchComponent,
    UserSearchConflictsComponent,
    SearchButtonComponent,
    ReviewDelegateComponent,
    UserSearchDialogComponent,
    ReviewFormComponent,
    CardViewComponent,
    ChipCheckboxComponent,
    RadioButtonComponent,
    FloatingSubmitBarComponent,
    PeopleTableComponent,
    ChairTabsComponent,
    ChairTabComponent,
    LazyLoadDirective,
    FixedPrefixDirective,
    PeopleTableFooterAddDirective,
    PeopleTableRowActionsDirective,
    PeopleTableBatchActionsDirective,
    CheckboxInputComponent,
    CustomFormComponent,
    AffiliationSearchComponent,
    SubSessionTextFilterPipe,
    SubSessionStatusFilterPipe,
    SessionSubsOrderPipe,
    CapitalizeFirstPipe,
    ColorPickerComponent,
    SessionSubmissionsPickerComponent,
    AddSessionSubmissionsDialogComponent,
    SubmissionsTableComponent,
    EventComponent,
    EventViewComponent,
    SubmissionStepComponent,
    ErrorComponent,
    UserFilterPipe,
    EmptyStateComponent,
    ReadableSizePipe,
    TruncatePipe,
    CurrencyFormatPipe,
    NumericInputComponent,
    ReviewViewComponent,
    UserReviewComponent,
    FixedTopProgressBarComponent,
    FileRulesTableComponent,
    SidesheetComponent,
    CardPlaceholderComponent,
    AdminComponent,
    AdminEventsListComponent,
    AdminEventViewComponent,
    EventAdminComponent,
    EventSubmissionsComponent,
    EventPeopleComponent,
    EventSettingsComponent,
    SidebarItemComponent,
    AdminEventSidebarComponent,
    EventBreadcrumbComponent,
    SidebarItemAdminComponent,
    RouterLinkDisableDirective,
    OverflowTooltipDirective,
    TruncateDirective,
    CollapseHeightDirective,
    StickyShadowDirective,
    StepperAutoHeightDirective,
    EventsFilterPipe,
    DefaultFilterPipe,
    ReplacePipe,
    SubmissionsListFilterComponent,
    ErrorSnackbarComponent,
    SearchResultsComponent,
    AdminNavbarComponent,
    ErrorSnackbarComponent,
    SearchResultsComponent,
    AdminEventCreateComponent,
    FormOverlayComponent,
    DatepickerInputComponent,
    DatetimepickerInputComponent,
    JemsCalendarHeaderComponent,
    ArrayChipInputComponent,
    EventSearchComponent,
    NotificationSnackbarComponent,
    SubmissionsListFilterComponent,
    ViewPreferencesPickerComponent,
    ChangePasswordProfileComponent,
    SubmissionCardNewComponent,
    ReviewCardNewComponent,
    SelectionActionsBarComponent,
    PaperStatusComponent,
    ChangeProfilePictureComponent,
    ErrorViewComponent,
    AssignReviewersComponent,
    CharacterCounterComponent,
    SubmissionEditComponent,
    RequestEventComponent,
    EventFormComponent,
    AdminEventRequestsComponent,
    ObjectToArrayPipe,
    CheckEventComponent,
    AdminLandingComponent,
    AdminSidebarComponent,
    AdminPageHeaderComponent,
    SegmentedControlComponent,
    AdminEventRequestComponent,
    EventSettingsEmailComponent,
    ActivateEmailComponent,
    EventTopicsComponent,
    EventChairsComponent,
    EventPublicationChairsComponent,
    EventTracksComponent,
    EventTracksEditComponent,
    AdminEventNavbarComponent,
    EventSendEmailComponent,
    EventMembersComponent,
    TpcInvitePanelComponent,
    AdminCreateSubmissionComponent,
    AdminSubmitSelectTrackComponent,
    SearchResultsInEventComponent,
    PeopleCardComponent,
    PaperGroupsComponent,
    PaperTitlePipe,
    UserEventTopicsComponent,
    EventUserTopicsComponent,
    PaperTitlePipe,
    EventTitlePipe,
    EventFormsComponent,
    TPCGroupsComponent,
    FormQuestionComponent,
    ReviewsConfigurationComponent,
    CustomFormTextQuestionComponent,
    CustomFormCheckboxQuestionComponent,
    CustomFormRadioQuestionComponent,
    CustomFormLabelQuestionComponent,
    ReportPrintingProblemComponent,
    PaperInfoComponent,
    SubmissionTopicsDisplayComponent,
    SubmissionAbstractDisplayComponent,
    SubmissionSessionDisplayComponent,
    SubmissionHeaderComponent,
    SubmissionAuthorsDisplayComponent,
    SubmissionExtraFieldsDisplayComponent,
    SubmissionExportViewComponent,
    PresentationCertificateComponent,
    CertificateSheetComponent,
    CertificateTemplateEditorComponent,
    StartEditReviewComponent,
    PaperReviewActionsComponent,
    PaperReviewListComponent,
    PreviewFormComponent,
    EditableInputComponent,
    SubmissionHistoryComponent,
    PreviewFormComponent,
    EditableInputComponent,
    PrintingProblemComponent,
    MissingManuscriptsComponent,
    UserViewComponent,
    PaperReviewHistoryComponent,
    ConflictInterestComponent,
    ConfirmationDialogComponent,
    ReviewConfirmationDialogComponent,
    ReviewStatusPerSubmissionComponent,
    ReviewStatusPerUserComponent,
    ConfirmReviewComponent,
    ConfirmTPCInviteRedirectComponent,
    NotifyReviewersComponent,
    AdminNotifyReviewersComponent,
    NotifyAuthorsComponent,
    AdminNotifyAuthorsComponent,
    SubmissionFieldsFilterComponent,
    FindReviewersByInterestComponent,
    ReviewerFinderComponent,
    ClaimReviewComponent,
    EventUserViewComponent,
    CreateUserComponent,
    TracksComponent,
    FileRulesViewComponent,
    EventBackButtonComponent,
    EditUserAffiliationComponent,
    SubmissionRankingComponent,
    AuthorsListComponent,
    SubmissionsUploadComponent,
    ReviewsUploadComponent,
    AssignReviewSuggestionComponent,
    SubmissionWithdrawalComponent,
    SubmissionDeleteComponent,
    DiscussionMessagesComponent,
    EmailPreviewComponent,
    SubmissionFilesComponent,
    AdminBibtexExportComponent,
    AdminDefaultEmailTemplatesComponent,
    RebuttalComponent,
    AdministratorsComponent,
    MergeUsersComponent,
    AdminDeletedSubmissionsComponent,
    ProxyBarComponent,
    AdminEventWizardComponent,
    FileRulesCompactComponent,
    ChartComponent,
    DashboardChartComponent,
    AdminEventSubconferencesComponent,
    ShowControlsErrorsComponent,
    UserDashboardComponent,
    ReviewsSmallTableComponent,
    SessionsComponent,
    EventSessionsComponent,
    FormSessionDialogComponent,
    FormSubSessionDialogComponent,
    GeneralSessionDialogComponent,
    EventProgramComponent,
    EmailStatusComponent,
    CountryPipe,
    CountryPurePipe,
    DateFormatPurePipe,
    UserAffiliationPipe,
    LandingProfileComponent,
    LoginHistoryComponent,
    DateFormatPipe,
    RelativeTimePipe,
    EventUrlPipe,
    AvatarProfileComponent,
    NumberInputComponent,
    SubmissionStatisticsComponent,
    SubmissionTopicStatisticsComponent,
    SubmissionTracksStatisticsComponent,
    SubmissionAuthorStatisticsComponent,
    InputFilterAutocompleteComponent,
    DialogTimezoneComponent,
    CreateAffiliationComponent,
    EventPublicationComponent,
    EventPublicationProceedingsComponent,
    CustomControlFieldEditComponent,
    DialogTimezoneComponent,
    SubmissionsListTableComponent,
    ProceedingStepComponent,
    ProceedingContentItemComponent,
    FileRulesCardComponent,
    NewGroupDialogComponent,
    SelectInputGroupComponent,
    PublicationFixedControlComponent,
    SubmissionPublicationSectionComponent,
    FileRulesCardInfoDialogComponent,
    ArticlesListCardDialogComponent,
    SelectInputGroupComponent,
    MenuSubmissionListComponent,
    ActionsMenuButtonComponent,
    NotifyPendingsComponent,
    ProxyHistoryComponent,
    DisplayPaperTitleComponent,
    DialogCreateUserComponent,
    PrintingProblemsComponent,
    ProceedingIndexItemComponent,
    TimeZoneInputComponent,
    PaperPublicationInfoComponent,
    ProceedingAuthorIndexItemComponent,
    ReviewerInvitationComponent,
    ReviewReclaimComponent,
    InputDropFileComponent,
    AdminUserSearchComponent,
    ChangeTracksDialogComponent,
    SubmissionsListTableV2Component,
    ScoreCellComponent,
    ScrollTopFabComponent,
    SubmissionsFieldFilterV2Component,
    ChartComponentV2Component,
    DashboardChartV2Component,
    ExportSubmissionsComponent,
    VerifySimilarityDialogComponent,
    ExportSubmissionsChooserComponent,
    MathCaptchaComponent,
    EditEmailComponent,
    DeletedSubmissionsComponent,
    ImportEmailTemplatesDialogComponent,
    EmailTemplateEditDialogComponent,
    VariableTemplateEditorComponent,
    EmailTemplatePreviewComponent,
    SupportButtonComponent,
    CustomSearchComponent,
    SupportButtonComponent,
    HelpdeskCardComponent,
    HelpDialogComponent,
    CommandPaletteComponent,
    TrackFileLabelComponent,
    UserNotificationsComponent,
    UserNotificationPreviewComponent,
    NotificationsCenterComponent
  ],
  imports: [
    DragDropModule,
    OverlayModule,
    NgxMailtoModule,
    BrowserModule,
    HttpClientModule,
    JwtModule.forRoot({
      config: getJwtConfig()
    }),
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    AvatarModule,
    InfiniteScrollModule,
    ContentLoaderModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: LazyTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ClickOutsideModule,
    EcoFabSpeedDialModule,
    NgxPrintModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatMomentModule,
    NgxMatNativeDateModule,   // sobrescreve pra Native adapter (o app usa `Date` nativo nos FormControls)
    ClipboardModule,
    ScrollingModule,
    ChartistModule,
    MarkdownModule.forRoot({ loader: HttpClientModule }),
    CalendarModule.forRoot({ provide: DateAdapter, useFactory: momentAdapterFactory }),
    ImageCropperModule,
    HammerModule,
    NgxChartsModule,
    RecaptchaModule
  ],
  providers: [
    // `touchstart` registrado pelo Angular vira PASSIVO — mata os avisos
    // "[Violation] non-passive event listener" que o `mwlDraggable` do
    // calendário gera por bloco. Ver o cabeçalho do plugin para o porquê de ser
    // seguro (a lib cancela o scroll no `touchmove`, não no `touchstart`).
    {
      provide: EVENT_MANAGER_PLUGINS,
      useClass: PassiveTouchEventsPlugin,
      multi: true
    },
    // mat-progress-bar monta `url({pathname}#{id})` pro pattern SVG. O
    // {pathname} vira URL absoluta e Chrome tenta baixar como arquivo (404).
    // Bug conhecido em SPAs. Fix: pathname vazio → referência local `#{id}`.
    {
      provide: MAT_PROGRESS_BAR_LOCATION,
      useValue: { getPathname: getPathnameMock }
    },
    CountryPipe,          // injetável pelo UserAffiliationPipe
    UserAffiliationPipe,
    AuthGuard,
    RedirectGuard,
    AdminGuard,
    CookieService,
    Title,
    RouteTitleGuard,
    UnsavedChangesGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptor,
      multi: true
    },
    ChairOrAdminGuard,
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorI18n
    },
    {
      provide: MAT_DATE_LOCALE,
      useClass: MomentDateAdapter,
      deps: [ MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS ],
    },
    // MAT_DATE_FORMATS (Material core) — controla os rótulos do `mat-calendar`,
    // incluindo `monthYearLabel` (título do painel, mês+ano). O provider anterior
    // passava `MAT_MOMENT_DATE_ADAPTER_OPTIONS` (que é um InjectionToken de
    // OPÇÕES do adapter, não um objeto de formatos) — bug pré-existente. Aqui
    // uma factory adapta o separador ao locale:
    //   pt-BR → "jun. de 2026"
    //   EN    → "Jun, 2026"
    // MAT_DATE_FORMATS (Material core) — Material 11 mat-datepicker convive com
    // o MomentDateAdapter provido por `provide: MAT_DATE_LOCALE, useClass: MomentDateAdapter`,
    // então aqui os formatos SÃO strings Moment (não Intl options).
    {
      provide: MAT_DATE_FORMATS,
      useFactory: () => {
        const isPt = (getLocale() || '').toLowerCase().startsWith('pt');
        const monthYearLabel = isPt ? 'MMM [de] YYYY' : 'MMM YYYY';
        return {
          parse: { dateInput: 'L' },
          display: {
            dateInput: 'L',
            monthYearLabel,
            dateA11yLabel: 'LL',
            monthYearA11yLabel: 'MMMM YYYY',
          },
        };
      }
    },
    // NGX_MAT_DATE_FORMATS — o adapter ativo é o NATIVE (o app usa `Date` nos
    // FormControls). Native usa `Intl.DateTimeFormat`, então os campos são
    // OBJETOS de options (não strings Moment). `monthYearLabel` como
    // `{ year: 'numeric', month: 'short' }` produz "jun. de 2026" em pt-BR
    // e "Jun 2026" em en — o header custom (`JemsCalendarHeaderComponent`)
    // remove o uppercase que a lib aplicaria por cima.
    {
      provide: NGX_MAT_DATE_FORMATS,
      useValue: {
        parse: { dateInput: { year: 'numeric', month: 'numeric', day: 'numeric' } },
        display: {
          dateInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
          monthYearLabel: { year: 'numeric', month: 'short' },
          dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
          monthYearA11yLabel: { year: 'numeric', month: 'long' },
        },
      }
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: getLocale()
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: Environment.recaptcha.siteKey
      } as RecaptchaSettings
    }
  ],
  entryComponents: [
    // `enableIvy: false` neste projeto: componente aberto via MatDialog PRECISA
    // estar aqui, senão o Angular não gera a factory e o diálogo não instancia.
    AddSessionSubmissionsDialogComponent,
    SubmissionExportViewComponent,   // instanciado dinamicamente por paper-info.downloadSubmissionHtmlPage
    JemsCalendarHeaderComponent,   // instanciado dinamicamente pelo ngx-mat-datetime-picker
    HelpdeskCardComponent,
    SidesheetComponent,
    ErrorSnackbarComponent,
    NotificationSnackbarComponent,
    ChangePasswordProfileComponent,
    CustomFormTextQuestionComponent,
    CustomFormCheckboxQuestionComponent,
    CustomFormRadioQuestionComponent,
    ReviewConfirmationDialogComponent,
    ConfirmationDialogComponent,
    SubmissionWithdrawalComponent,
    SubmissionDeleteComponent,
    FormSessionDialogComponent,
    FormSubSessionDialogComponent,
    GeneralSessionDialogComponent,
    CustomControlFieldEditComponent,
    NewGroupDialogComponent,
    FileRulesCardInfoDialogComponent,
    ArticlesListCardDialogComponent,
    DialogCreateUserComponent,
    ReviewDelegateComponent,
    UserSearchDialogComponent,
    PrintingProblemsComponent,
    CreateAffiliationComponent,
    ChangeTracksDialogComponent,
    VerifySimilarityDialogComponent,
    ReviewReclaimComponent,
    EditEmailComponent,
    HelpDialogComponent,
    UserNotificationPreviewComponent,
    ImportEmailTemplatesDialogComponent,
    EmailTemplateEditDialogComponent,
    VariableTemplateEditorComponent,
    EmailTemplatePreviewComponent,
    HelpDialogComponent,
    CommandPaletteComponent,
    ReviewerFinderComponent,
    NotificationsCenterComponent
  ],
  bootstrap: [BaseComponent]
})

export class AppModule {
  constructor(private iconRegistry: MatIconRegistry, private sanitizer: DomSanitizer) {
    iconRegistry.registerFontClassAlias('jems3s', 'jems3s');
    iconRegistry.registerFontClassAlias('jems3r', 'jems3r');
  }
 }
